/**
 * Animated Tabs - табы, которые ужимаются, когда строка не влезает в контейнер, вместо того
 * чтобы уезжать в горизонтальный скролл с обрезанным последним табом.
 * Ступени: подписи + обычные отступы → подписи + поджатые отступы → только иконки
 * (у активного таба подпись остаётся). На мобильных последняя ступень включена всегда.
 */

import React, { useLayoutEffect, useRef, useState } from 'react';
import { Box, Tabs, Tab, useTheme, useMediaQuery } from '@mui/material';
import { TEXT_COLORS, BORDER_COLORS } from '../../utils/colors';
import { TAB_SIZES } from '../../utils/responsive';

export interface AnimatedTabItem {
  value: string;
  label: string;
  icon: React.ReactElement;
}

interface AnimatedTabsProps {
  value: string;
  onChange: (value: string) => void;
  tabs: AnimatedTabItem[];
  sx?: object;
}

export const AnimatedTabs: React.FC<AnimatedTabsProps> = ({
  value,
  onChange,
  tabs,
  sx,
}) => {
  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.up('sm'));
  const containerRef = useRef<HTMLDivElement>(null);

  // Плотность строки табов: 0 — подписи и обычные отступы, 1 — подписи и поджатые отступы,
  // 2 — только иконки (подпись остаётся у активного). Ступень выбирается по факту переполнения:
  // сперва пробуем сэкономить на отступах и лишь потом убираем подписи.
  const [density, setDensity] = useState(0);
  const [, setRemeasureTick] = useState(0);
  const lastWidthRef = useRef(0);
  const tabsKey = tabs.map((tab) => `${tab.value}:${tab.label}`).join('|');

  // Сменился набор табов — прошлый замер больше не про них
  useLayoutEffect(() => {
    lastWidthRef.current = 0;
    setDensity(0);
  }, [tabsKey]);

  // Меряем на каждом рендере: ширина контейнера меняется без участия пропсов
  // (открылась боковая панель детали, ресайз окна), а список зависимостей такие случаи пропускает.
  useLayoutEffect(() => {
    if (!isSm) return; // на мобильных подписи и так свёрнуты
    const container = containerRef.current;
    const scroller = container?.querySelector('.MuiTabs-scroller');
    if (!container || !scroller) return;

    // Контейнер блочный: его ширину задаёт колонка, а не содержимое, — обратной связи нет
    if (container.clientWidth !== lastWidthRef.current) {
      lastWidthRef.current = container.clientWidth;
      if (density !== 0) {
        setDensity(0);
        return;
      }
    }
    // Ступеней две, каждая укорачивает строку — цикл конечен
    if (density < 2 && scroller.scrollWidth > scroller.clientWidth + 1) {
      setDensity(density + 1);
    }
  });

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const observer = new ResizeObserver(() => setRemeasureTick((tick) => tick + 1));
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  // Подписи показываем, только если есть и брейкпоинт, и место
  const showLabels = isSm && density < 2;
  const tightPadding = !isSm || density >= 1;

  // Convert vertical wheel scroll to horizontal on MUI Tabs scroller
  const handleWheel = (e: React.WheelEvent) => {
    const scroller = containerRef.current?.querySelector('.MuiTabs-scroller') as HTMLElement;
    if (scroller && e.deltaY !== 0) {
      e.preventDefault();
      scroller.scrollLeft += e.deltaY;
    }
  };

  return (
    <Box ref={containerRef} onWheel={handleWheel}>
    <Tabs
      value={value}
      onChange={(_, newValue) => onChange(newValue)}
      variant="scrollable"
      scrollButtons={false}
      sx={{
        minHeight: TAB_SIZES.height,
        borderBottom: 1,
        mb: 2,
        borderColor: BORDER_COLORS.DEFAULT,
        '& .MuiTabs-indicator': {
          backgroundColor: '#ffffff',
        },
        ...sx,
      }}
    >
      {tabs.map((tab) => {
        const isActive = value === tab.value;

        return (
          <Tab
            key={tab.value}
            value={tab.value}
            icon={tab.icon}
            iconPosition="start"
            label={
              <Box
                component="span"
                sx={{
                  display: 'inline-block',
                  maxWidth: showLabels ? 'none' : (isActive ? 120 : 0),
                  opacity: showLabels ? 1 : (isActive ? 1 : 0),
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  transition: 'max-width 0.3s ease, opacity 0.3s ease',
                }}
              >
                {tab.label}
              </Box>
            }
            sx={{
              minHeight: TAB_SIZES.height,
              minWidth: 'auto',
              fontSize: TAB_SIZES.fontSize,
              px: tightPadding ? TAB_SIZES.px.xs : TAB_SIZES.px,
              py: TAB_SIZES.py,
              color: TEXT_COLORS.SECONDARY,
              transition: 'min-width 0.3s ease',
              '&.Mui-selected': { color: '#ffffff' },
              '& svg': {
                fontSize: isSm ? TAB_SIZES.iconSize.sm : TAB_SIZES.iconSize.xs,
              },
              '& .MuiTab-iconWrapper': {
                mr: showLabels ? 0.75 : (isActive ? 0.5 : 0),
                transition: 'margin 0.3s ease',
              },
            }}
          />
        );
      })}
    </Tabs>
    </Box>
  );
};
